import { formatDuration, type Plan } from "@roxpacer/core";
import { useEstimate } from "../lib/use-estimate";

export function Summary({ plan }: { plan: Plan }) {
  const estimate = useEstimate(plan);

  const parts = [
    { label: "Running", sec: estimate.runSec, color: "bg-run" },
    { label: "Stations", sec: estimate.stationSec, color: "bg-station" },
    { label: "Roxzone", sec: estimate.roxzoneSec, color: "bg-roxzone" },
    ...(plan.format === "relay"
      ? [{ label: "Handovers", sec: estimate.handoverSec, color: "bg-handover" }]
      : []),
  ];

  return (
    <section className="card p-4" aria-labelledby="summary-heading">
      <h2 id="summary-heading" className="label">
        Estimated finish
      </h2>
      <p
        className="mt-1 text-5xl font-semibold tracking-tight tabular-nums"
        data-testid="total-time"
        aria-live="polite"
      >
        {formatDuration(estimate.totalSec, { alwaysHours: true })}
      </p>
      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        {parts.map((part) => (
          <div key={part.label} className="flex items-center justify-between gap-2">
            <dt className="flex items-center gap-1.5 text-ink-2">
              <span aria-hidden className={`h-3 w-1 rounded-full ${part.color}`} />
              {part.label}
            </dt>
            <dd className="font-medium tabular-nums">{formatDuration(part.sec)}</dd>
          </div>
        ))}
      </dl>

      {plan.athletes.length > 1 && (
        <table className="mt-4 w-full text-sm">
          <caption className="label mb-1 text-left">Work per athlete</caption>
          <thead>
            <tr className="text-left text-xs text-muted">
              <th className="font-normal">Athlete</th>
              <th className="text-right font-normal">Running</th>
              <th className="text-right font-normal">Stations</th>
            </tr>
          </thead>
          <tbody>
            {estimate.athletes.map((load) => (
              <tr key={load.athleteId} className="border-t border-line">
                <td className="max-w-32 truncate py-1">
                  {plan.athletes.find((a) => a.id === load.athleteId)?.name}
                </td>
                <td className="text-right tabular-nums">{formatDuration(load.runSec)}</td>
                <td className="text-right tabular-nums">{formatDuration(load.stationSec)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {plan.format === "doubles" && (
        <p className="mt-2 text-xs text-muted">Station time is weighted by each work split.</p>
      )}
    </section>
  );
}
